import { useState } from 'react';
import { checkLabAvailability } from '../services/labApi';
import { resolveSelectedLabMethod } from '../services/labCheckoutFlow';
import { resolveStateFromZip } from '../services/zipStateResolver';
import { parseRateLimitError } from '../../../utils/rateLimit';

/**
 * Owns collection-method / serviceability state for lab checkout:
 * ZIP entry, availability lookup, and the selected method / draw site.
 *
 * @param slug storefront slug
 * @param lab loaded lab object (for collectionOptions)
 * @param getAddressState () => current address.state
 * @param setAddress identity hook's address setter (keeps zip in sync)
 * @param onError (message) => void  used for inline ZIP validation
 */
export default function useLabCheckoutCollection({ slug, lab, getAddressState, setAddress, setAddressVerified, onError }) {
  const [zip, setZip] = useState('');
  const [availResult, setAvailResult] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedDrawSite, setSelectedDrawSite] = useState(null);

  const handleCheckAvailability = async (zipVal, options = {}) => {
    const z = (zipVal || zip || '').trim();
    if (z.length < 5) { if (onError) onError('Enter a valid 5-digit ZIP code.'); return; }
    let state = options.stateOverride ?? (
      typeof getAddressState === 'function' ? getAddressState() : ''
    );
    setAvailLoading(true);
    setAvailResult(null);
    setSelectedMethod(null);
    setSelectedDrawSite(null);
    if (setAddressVerified) setAddressVerified(false);
    if (onError) onError('');
    try {
      if (!options.stateOverride) {
        try {
          const resolved = await resolveStateFromZip(z);
          state = resolved.state;
          setAddress(prev => ({
            ...prev,
            zip: z,
            state: resolved.state,
            city: resolved.city || prev.city,
          }));
        } catch (error) {
          const rateLimitMsg = parseRateLimitError(error);
          if (rateLimitMsg) {
            setAvailResult({ is_available: false, reason: rateLimitMsg, reason_code: 'rate_limited' });
            return;
          }
          // Fall through to backend ZIP resolution
        }
      } else {
        setAddress(prev => ({ ...prev, zip: z, state }));
      }
      const result = await checkLabAvailability(slug, z, state, '25');
      const detectedState = result.resolved_state || state;
      const detectedCity = result.resolved_city;
      if (detectedState) {
        setAddress(prev => ({
          ...prev,
          zip: z,
          state: detectedState,
          ...(detectedCity && !prev.city ? { city: detectedCity } : {}),
        }));
      }
      setAvailResult({ ...result, checkedZip: z });
      const restoredSelection = resolveSelectedLabMethod({ lab, result, methodHint: options.methodHint });
      if (restoredSelection) {
        setSelectedMethod(restoredSelection);
      } else if (result.is_available && !result.is_combined && lab?.collectionOptions?.[0]) {
        setSelectedMethod(lab.collectionOptions[0]);
      } else if (result.is_combined && result.serviceable_methods?.length === 1) {
        const m = result.serviceable_methods[0];
        const opt = lab.collectionOptions?.find(o => o.method === m.method) || m;
        setSelectedMethod(opt);
      }
    } catch (err) {
      const rateLimitMsg = parseRateLimitError(err);
      setAvailResult({
        is_available: false,
        reason: rateLimitMsg || err.message,
        reason_code: rateLimitMsg ? 'rate_limited' : 'zip_state_resolution_failed',
      });
    } finally {
      setAvailLoading(false);
    }
  };

  const handleZipChange = (value) => {
    setZip(value);
    setAvailResult(null);
    setSelectedMethod(null);
    setSelectedDrawSite(null);
    if (setAddressVerified) setAddressVerified(false);
    if (onError) onError('');
    // ponytail: clear street/city so stale address from old ZIP isn't submitted with a new one
    setAddress(prev => ({ ...prev, zip: value, street: '', city: '' }));
  };

  const handleStateChange = (state) => {
    if (onError) onError('');
    if (setAddressVerified) setAddressVerified(false);
    setAddress(prev => ({ ...prev, state }));
    if ((zip || '').trim().length === 5 && state) {
      handleCheckAvailability(zip, { stateOverride: state });
    }
  };

  return {
    zip, setZip,
    availResult,
    availLoading,
    selectedMethod, setSelectedMethod,
    selectedDrawSite, setSelectedDrawSite,
    handleCheckAvailability,
    handleZipChange,
    handleStateChange,
    onStateChange: handleStateChange,
  };
}
